import { useCallback, useMemo } from "react";
import { OverviewType, useAppSelector } from "../../app";
import { DEFAULT_ORDER_COLUMNS, REQUIRED_COLUMNS } from "../constants";
import { ColumnItem } from "../interfaces/overview";

const useOverviewMapColumns = () => {
  const { overviewType } = useAppSelector((state) => state.documentSetsReducer);
  const { overviewMap } = useAppSelector((state) => state.appReducer);
  const { archiveOrderColumns, overviewOrderColumns } = useAppSelector(
    (state) => state.userReducer.userSettings,
  );

  const overviewMapKeys = overviewMap.map((o) => o.key);

  const defaultSort = useMemo(() => {
    const defaultOrderMapKeys = DEFAULT_ORDER_COLUMNS.map((doc) => doc.key);
    return [...overviewMap].sort((a, b) => {
      const indexA = defaultOrderMapKeys.indexOf(a.key);
      const indexB = defaultOrderMapKeys.indexOf(b.key);

      return indexA - indexB;
    });
  }, [overviewMap]);

  // A column belongs on the overview unless it is archive-only. The only
  // archive-only columns allowed through are showInError columns on the Error
  // view (e.g. approvedDate). Everything else must stay off the overview.
  const isVisibleInOverview = useCallback(
    (item: ColumnItem) =>
      !item.archiveOnly ||
      (overviewType === OverviewType.Error && item.metadata?.showInError),
    [overviewType],
  );

  const overviewOrderedMap = useCallback(
    (filter = true) => {
      if (overviewOrderColumns?.length) {
        const overviewOrderColumnsKeys = overviewOrderColumns
          .filter((ooc) => {
            if (filter) {
              return ooc.show;
            }

            return true;
          })
          .filter((ooc) => overviewMapKeys.includes(ooc.key))
          .map((ooc) => ooc.key);
        return [...overviewMap]
          .filter(
            (item) =>
              isVisibleInOverview(item) &&
              overviewOrderColumnsKeys.includes(item.key),
          )
          .sort((a, b) => {
            const indexA = overviewOrderColumnsKeys.indexOf(a.key);
            const indexB = overviewOrderColumnsKeys.indexOf(b.key);

            return indexA - indexB;
          });
      }

      // Fallback: default order, but apply the same visibility filter so
      // archive-only columns never leak onto the overview when the user has
      // no saved column order yet.
      return defaultSort.filter(isVisibleInOverview);
    },
    [
      overviewOrderColumns,
      defaultSort,
      overviewMap,
      overviewMapKeys,
      isVisibleInOverview,
    ],
  );

  const archivedOrderedMap = useCallback(
    (filter = true) => {
      if (archiveOrderColumns?.length) {
        const archiveOrderColumnKeys = archiveOrderColumns
          .filter((ooc) => {
            if (filter) {
              return ooc.show;
            }

            return true;
          })
          .filter((ooc) => overviewMapKeys.includes(ooc.key))
          .map((ooc) => ooc.key);
        return [...overviewMap]
          .filter((item) => archiveOrderColumnKeys.includes(item.key))
          .sort((a, b) => {
            const indexA = archiveOrderColumnKeys.indexOf(a.key);
            const indexB = archiveOrderColumnKeys.indexOf(b.key);

            return indexA - indexB;
          });
      }

      return defaultSort;
    },
    [archiveOrderColumns, defaultSort, overviewMap, overviewMapKeys],
  );

  const tableItems = useMemo(() => {
    if (overviewType === OverviewType.Archived) {
      return [
        REQUIRED_COLUMNS.bulkDelete,
        REQUIRED_COLUMNS.duplicate,
        ...archivedOrderedMap(),
        REQUIRED_COLUMNS.remove,
      ];
    }

    return [
      REQUIRED_COLUMNS.bulkDelete,
      REQUIRED_COLUMNS.duplicate,
      ...overviewOrderedMap(),
      REQUIRED_COLUMNS.remove,
    ];
  }, [archivedOrderedMap, overviewOrderedMap, overviewType]);

  const orderItems = useMemo(() => {
    if (overviewType === OverviewType.Archived) {
      return archivedOrderedMap(false);
    }

    return overviewOrderedMap(false);
  }, [archivedOrderedMap, overviewOrderedMap, overviewType]);

  return { tableItems, orderItems };
};

export default useOverviewMapColumns;
